import { useMemo } from 'react';
import {
  EmptyFilesUpload,
  FacialAuthenticationStatus,
  MimeType,
  TrackingFamily,
} from '@facephi/inphinite-common';
import { Icon, Label } from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ImageLoad } from './ImageLoad';
import { FingerprintValidationState, infoFingerprint, tInfo } from './model';
import { OperationsCardIcon } from './operationsCards/Styles';
import {
  FacialCheckGrid as FingerprintCheckGrid,
  FacialContainer as FingerprintContainer,
  FacialResultIconWrapper as FingerprintResultIconWrapper,
  InfoContainer,
} from './Styles';
import { useOperations } from '../../providers';
import { availableFeatureFlags } from '../../state/constants';

type Props = {
  state?: FingerprintValidationState[];
  loading?: boolean;
  family?: string;
};

const ImageSkeleton = () => (
  <div>
    <Skeleton width={120} height={120} borderRadius={8} />
    <div style={{ marginTop: '0.8rem' }}>
      <Skeleton width="50%" height={18} borderRadius={8} />
    </div>
  </div>
);

export const FingerprintValidation = ({ state, loading, family }: Props) => {
  const { t } = useTranslation();
  const [{ authStatus, id, similarity, assetsUrl }] = state || [{}];
  const { token, activeFlags } = useOperations();
  const emptyState = !state || !state.length;

  const [firstAsset, secondAsset] = useMemo(
    () =>
      (assetsUrl ?? []).map((url, index) =>
        url
          ? {
              url,
              type: index === 0 ? 'FINGERPRINT' : 'FINGERPRINT_REFERENCE',
              contentType: MimeType.jpeg,
              characteristics: null,
            }
          : undefined,
      ),
    [assetsUrl],
  );

  const similarityResult: string = useMemo(
    () =>
      activeFlags
        ? activeFlags.includes(availableFeatureFlags.similarity)
          ? ` (${
              (similarity && similarity > 0
                ? (similarity * 100).toPrecision(4)
                : 0
              ).toString() || ''
            } %)`
          : ''
        : '',
    [similarity, activeFlags],
  );

  const getLoadingImages = () => (
    <>
      <ImageSkeleton />
      <Skeleton width={40} height={40} circle />
      <ImageSkeleton />
    </>
  );

  const info: tInfo | undefined = useMemo(() => {
    if (emptyState) return undefined;
    if (!authStatus) return infoFingerprint['notAble'];
    const type =
      authStatus === FacialAuthenticationStatus.positive
        ? 'match'
        : authStatus === FacialAuthenticationStatus.negative
          ? 'notMatch'
          : 'notAble';
    return infoFingerprint[type];
  }, [state, emptyState, authStatus]);

  const getImages = () => (
    <>
      <FingerprintContainer
        flexDirection="column"
        rowGap="0.8"
        className="facial-match-animated"
      >
        {firstAsset ? (
          <ImageLoad asset={firstAsset} token={token} />
        ) : (
          <EmptyFilesUpload />
        )}
      </FingerprintContainer>

      {info && (
        <OperationsCardIcon
          $bgColor={info.backgroundColor}
          $remSize="6rem"
          key={`fingerprint-provider-${id}`}
          id="fingerprint-match-icon"
          $borderRadius="1.7rem"
          className="facial-match-animated"
        >
          <Icon
            iconName={info.iconName}
            size={info.size}
            color={info?.iconColor}
            weight={info.weight}
          />
        </OperationsCardIcon>
      )}
      {!emptyState && (
        <FingerprintContainer
          flexDirection="column"
          rowGap="0.8"
          className="facial-match-animated"
          key={`fingerprint-image-${secondAsset?.url || 'empty'}`}
        >
          {secondAsset ? (
            <ImageLoad asset={secondAsset} token={token} />
          ) : (
            <EmptyFilesUpload />
          )}
        </FingerprintContainer>
      )}
    </>
  );

  return (
    <>
      <FingerprintCheckGrid
        alignItems="center"
        columnGap="0.8"
        justifyContent={emptyState ? 'center' : 'space-between'}
        gridTemplateColumns={emptyState ? '12rem' : '12rem auto 12rem'}
        gridTemplateRows="10rem auto"
        rowGap="1.2"
      >
        {loading ? getLoadingImages() : getImages()}
        {!loading && (
          <>
            <Label className="asset-text" fontSize="12">
              {`${t('Fingerprint')} 1`}
            </Label>
            {!emptyState && (
              <Label
                fontSize="12"
                className="asset-text facial-match-animated"
                id="second-image-text"
              >
                {`${t('Fingerprint')} 2`}
              </Label>
            )}
          </>
        )}
      </FingerprintCheckGrid>
      {loading ? (
        <div style={{ marginTop: '2rem', textAlign: 'center' }}>
          <Skeleton width="80%" height={18} borderRadius={8} />
        </div>
      ) : (
        info && (
          <InfoContainer
            $infoBackground={info?.backgroundColor}
            $infoColor={info?.iconColor}
            rowGap="0.8"
            flexDirection="row"
            alignItems="center"
            key={`fingerprint-provider-${id}`}
          >
            <FingerprintResultIconWrapper>
              <Icon
                iconName="ChatsCircle"
                color={info?.iconColor}
                size="32"
                weight="thin"
              />
            </FingerprintResultIconWrapper>
            <Label fontSize="12" semibold>
              {`${t(
                (family === TrackingFamily.AUTHENTICATION
                  ? info?.message_authentication
                  : info?.message) as string,
              )} ${similarityResult}`}
            </Label>
          </InfoContainer>
        )
      )}
    </>
  );
};
